import {computed, observable, reaction, toJS, transaction} from 'mobx';
import {AnalyticsTab, DiagramMode, Store} from './store';
import {DateRange} from './TimeIntervalStore';
import {BackendFilter, DataKey} from './GeneralSelectorTableStore';
import moment from 'moment';
import uniqid from 'uniqid';
import {Omit} from '../types/Utility';
import {DateUtils} from '../utility/DateUtils';
import _ from 'lodash';
import {chunkProcessor} from 'mobx-utils';
import {TableSort} from '../types/Table';
import {isKPI, KPI, ProductKPI, SearchtermKPI} from '../kpi';
import {AsyncStorage, userKey} from '../storage/storage';
import {ChannelState} from './ChannelStore';
import {CompareData} from './CompareStore';

export enum MemoryBarTab {
    HISTORY = 'history',
    FAVORITES = 'favorites',
}

export interface HistoryHandler {
    getPartialHistory(): Partial<HistoryItem>;
    applyHistory(history: HistoryItem): void;
    afterApplyHistory?(history: HistoryItem): void;
}

export interface TimestampRange {
    startDate?: number;
    endDate?: number;
}

export interface ActiveDiagramItems {
    diagrams: SerializedLineDiagramRequest[];
    channelDiagrams: SerializedChannelDiagramRequest[];
    showAbsoluteValues: boolean;
}

export type SerializedCompareData = Omit<CompareData, 'compareEnabled'>;

export interface HistoryItem {
    id: string;
    tableLayout?: string;
    activeDiagramItems: ActiveDiagramItems;
    mainDateRange: DateRange;
    compareDateRange: DateRange;
    allowDateCompareRange: boolean;
    diagramMode: DiagramMode;
    channel?: string;
    analyticsTab: AnalyticsTab;
    activeFilters: BackendFilter[];
    activeSearch: string;
    activeTableSort?: TableSort;
    timestamp: number;
    compareData?: SerializedCompareData;
}

export const isSerializedLineDiagramRequest = (value: any): value is SerializedLineDiagramRequest => {
    return value && typeof value.channel === 'string' && typeof value.searchtermOrProductId === 'string' && value.kpi && isKPI(value.kpi);
};

export interface SerializedLineDiagramRequest {
    channel: string;
    searchtermOrProductId: string;
    kpi: KPI;
    testId?: string;
    variantId?: string;
}

export interface SerializedChannelDiagramRequest {
    channel: string;
    kpi: KPI;
    testId?: string;
    variantId?: string;
}

interface SerializedActiveDiagramItems {
    showAbsoluteValues: boolean;
    diagrams: SerializedLineDiagramRequest[];
    channelDiagrams: SerializedChannelDiagramRequest[];
}

export type SerializedMemoryItem =
    | (Omit<BookmarkItem, 'mainDateRange' | 'compareDateRange' | 'activeDiagramItems'> & {
          mainDateRange?: TimestampRange;
          compareDateRange?: TimestampRange;
          activeDiagramItems: SerializedActiveDiagramItems;
      })
    | (Omit<HistoryItem, 'mainDateRange' | 'compareDateRange' | 'activeDiagramItems'> & {
          mainDateRange?: TimestampRange;
          compareDateRange?: TimestampRange;
          activeDiagramItems: SerializedActiveDiagramItems;
      });

export type BookmarkItem = HistoryItem & {name: string};

export type HistoryOrBookmarkItem = HistoryItem | BookmarkItem;

interface CurrentHistorySettings {
    [analyticsTab: string]: HistoryItem;
}

export interface MemoryItemConversion {
    favoriteItemWasAdded: boolean;
    favoriteItemWasDowngradedToHistoryItem: boolean;
}

export enum MemoryItemConversionType {
    TO_HISTORY = 'toHistory',
    TO_FAVORITE = 'toFavorite',
}

class MemoryBarStore {
    private handlers: HistoryHandler[] = [];
    @observable public sidebarOpen = false;
    @observable public activeMemoryBarTab: MemoryBarTab = MemoryBarTab.HISTORY;
    @observable public historyItems: HistoryItem[] = [];
    @observable public bookmarkItems: BookmarkItem[] = [];
    @observable public historyItemAdded = false;
    @observable private batchApplyPartialHistory: Array<Partial<HistoryItem>> = [];
    @observable public searchText = '';
    private lastClickedItem?: HistoryItem;
    private searchTextTimeoutId = 0;

    public setSearchText = (text: string) => {
        clearTimeout(this.searchTextTimeoutId);
        this.searchTextTimeoutId = window.setTimeout(() => {
            this.searchText = text;
        }, 200);
    };

    @computed
    public get filteredHistoryItems(): HistoryItem[] {
        return this.historyItems.filter(this.searchFilter);
    }

    @computed
    public get filteredBookmarkItems(): BookmarkItem[] {
        return this.bookmarkItems.filter(this.searchFilter);
    }

    @observable
    public memoryItemConversion: MemoryItemConversion = {
        favoriteItemWasAdded: false,
        favoriteItemWasDowngradedToHistoryItem: false,
    };
    private memoryItemConversionTimer = 0;

    private memoryItemsLoadedFromServer = false;
    private currentHistoryItemSettings: CurrentHistorySettings = {};

    constructor(
        private readonly mainStore: Store,
        private storage: AsyncStorage,
        protected readonly channelProvider: ChannelState,
        private checkIfDataKeyExistsForAnalyticsTab: (dataKey: DataKey, analyticsTab: AnalyticsTab) => boolean
    ) {
        chunkProcessor(this.batchApplyPartialHistory, (chunk) => this.handleChunk(chunk), 100);
    }

    public changeActiveMemoryBarTab = (newMemoryBarTab: MemoryBarTab) => {
        this.activeMemoryBarTab = newMemoryBarTab;
    };

    public memoryItemConversionOccured = (conversionType: MemoryItemConversionType): void => {
        if (conversionType === MemoryItemConversionType.TO_FAVORITE) {
            this.memoryItemConversion.favoriteItemWasAdded = true;
        } else if (conversionType === MemoryItemConversionType.TO_HISTORY) {
            this.memoryItemConversion.favoriteItemWasDowngradedToHistoryItem = true;
        }
        clearTimeout(this.memoryItemConversionTimer);
        this.memoryItemConversionTimer = window.setTimeout(() => {
            this.memoryItemConversion = {
                favoriteItemWasDowngradedToHistoryItem: false,
                favoriteItemWasAdded: false,
            };
        }, 600);
    };

    public setSidebarOpen = (open: boolean): void => {
        this.sidebarOpen = open;
    };

    public toggleSidebar() {
        this.setSidebarOpen(!this.sidebarOpen);
    }

    public register(handler: HistoryHandler): void {
        this.handlers.push(handler);
        reaction(handler.getPartialHistory, (partial) => this.batchApplyPartialHistory.push(partial));
    }

    private handleChunk = (chunk: Array<Partial<HistoryItem>>) => {
        const merged = chunk.reduce((all, current) => ({...all, ...current}), {});
        this.savePartialHistory(merged);
    };

    public createInitialHistoryItems(getTableLayoutForAnalyticsTab: (analyticsTab: AnalyticsTab) => string) {
        const currentHistoryItemSettings: CurrentHistorySettings = {};
        const {mainDateRange, compareDateRange} = this.mainStore.timeIntervalStore;
        [AnalyticsTab.SearchView, AnalyticsTab.ProductView, AnalyticsTab.CampaignView].forEach((viewKey) => {
            currentHistoryItemSettings[viewKey.toString()] = {
                id: uniqid(),
                diagramMode: DiagramMode.Hourly,
                activeDiagramItems: {
                    diagrams: [],
                    channelDiagrams: [],
                    showAbsoluteValues: true,
                },
                tableLayout: getTableLayoutForAnalyticsTab(viewKey),
                allowDateCompareRange: this.mainStore.timeIntervalStore.allowDateCompareRange,
                mainDateRange: DateUtils.copyDateRange(mainDateRange),
                compareDateRange: DateUtils.copyDateRange(compareDateRange),
                channel: undefined,
                analyticsTab: viewKey,
                activeFilters: [], // attribute, comparator, value
                activeSearch: '',
                timestamp: 0,
            };
        });

        this.currentHistoryItemSettings = currentHistoryItemSettings;
    }

    public loadHistoryItemsAndBookmarks(): void {
        Promise.all([this.storage.getItem(userKey('historyItems'), []), this.storage.getItem(userKey('bookmarkItems'), [])]).then(
            ([serializedHistoryItems, serializedBookmarkItems]) => {
                this.historyItems = this.deserializeMemoryItem(serializedHistoryItems) as HistoryItem[];
                this.bookmarkItems = this.deserializeMemoryItem(serializedBookmarkItems) as BookmarkItem[];
                this.memoryItemsLoadedFromServer = true;
            }
        );
    }

    private updateSelectedChannelForCurrentHistoryItemsSettings(historyChange: Partial<HistoryItem>) {
        Object.keys(this.currentHistoryItemSettings).forEach((analyticsTab) => {
            this.currentHistoryItemSettings[analyticsTab] = {
                ...this.currentHistoryItemSettings[analyticsTab],
                channel: historyChange.channel,
            };
        });
    }

    private updateCompareSettings(historyChange: Partial<HistoryItem>) {
        if (Object.keys(historyChange).some((key) => key === 'compareData')) {
            Object.keys(this.currentHistoryItemSettings).forEach((analyticsTab) => {
                this.currentHistoryItemSettings[analyticsTab] = {
                    ...this.currentHistoryItemSettings[analyticsTab],
                    compareData: historyChange.compareData,
                };
            });
        }
    }

    private savePartialHistory = (historyChange: Partial<HistoryItem>) => {
        if (historyChange.channel) {
            this.updateSelectedChannelForCurrentHistoryItemsSettings(historyChange);
        }

        this.updateCompareSettings(historyChange);

        const activeAnalyticsTab = this.mainStore.activeAnalyticsTab;
        const currentHistoryItem = this.currentHistoryItemSettings[activeAnalyticsTab];

        if (activeAnalyticsTab === AnalyticsTab.Dashboard || !currentHistoryItem) {
            return;
        }

        const newHistoryEntry = {
            ...currentHistoryItem,
            mainDateRange: currentHistoryItem.mainDateRange && DateUtils.copyDateRange(currentHistoryItem.mainDateRange),
            compareDateRange: currentHistoryItem.compareDateRange && DateUtils.copyDateRange(currentHistoryItem.compareDateRange),
            ...historyChange,
            id: uniqid(),
            timestamp: Date.now(),
        };

        const currentHistoryItemWillChange = this.historyItemsAreDifferent(newHistoryEntry, currentHistoryItem);
        const lastHistoryItemIsDifferentFromNewOne = this.historyItemsAreDifferent(
            newHistoryEntry,
            this.historyItems.length > 0 ? this.historyItems[0] : undefined
        );

        const isDifferentToClickedHistoryItem = this.historyItemsAreDifferent(newHistoryEntry, this.lastClickedItem);

        this.currentHistoryItemSettings[activeAnalyticsTab] = newHistoryEntry;

        if (
            this.allowHistorySettingSave(newHistoryEntry) &&
            lastHistoryItemIsDifferentFromNewOne &&
            currentHistoryItemWillChange &&
            isDifferentToClickedHistoryItem
        ) {
            this.historyItems.unshift(newHistoryEntry);
            this.saveHistory();
            // for sidebar opening animation
            this.historyItemAdded = true;
            setTimeout(() => {
                this.historyItemAdded = false;
            }, 1000);
        }

        this.lastClickedItem = undefined;
    };

    private historyItemsAreDifferent(firstHistoryItem: HistoryItem, secondHistoryItem?: HistoryItem): boolean {
        const removeUndefinedKeys = (obj: any): any => {
            if (!obj) {
                return obj;
            }

            Object.keys(obj).forEach((key) => {
                if (!obj[key]) {
                    delete obj[key];
                }
            });
            return obj;
        };

        if (secondHistoryItem !== undefined) {
            const directAttributeCompare = () =>
                secondHistoryItem.allowDateCompareRange === firstHistoryItem.allowDateCompareRange &&
                secondHistoryItem.diagramMode === firstHistoryItem.diagramMode &&
                secondHistoryItem.channel === firstHistoryItem.channel &&
                secondHistoryItem.analyticsTab === firstHistoryItem.analyticsTab &&
                secondHistoryItem.activeSearch === firstHistoryItem.activeSearch &&
                secondHistoryItem.tableLayout === firstHistoryItem.tableLayout &&
                _.isEqual(toJS(removeUndefinedKeys(secondHistoryItem.activeTableSort)), toJS(removeUndefinedKeys(firstHistoryItem.activeTableSort)));
            if (
                DateUtils.dateRangeIsSame(secondHistoryItem.mainDateRange, firstHistoryItem.mainDateRange) &&
                DateUtils.dateRangeIsSame(secondHistoryItem.compareDateRange, firstHistoryItem.compareDateRange) &&
                directAttributeCompare() &&
                this.activeDiagramItemsAreEqual(secondHistoryItem.activeDiagramItems, firstHistoryItem.activeDiagramItems) &&
                this.activeFiltersAreEqual(secondHistoryItem.activeFilters, firstHistoryItem.activeFilters) &&
                _.isEqual(toJS(firstHistoryItem.compareData), toJS(secondHistoryItem.compareData))
            ) {
                return false;
            }
        }
        return true;
    }

    private activeDiagramItemsAreEqual(firstDiagramItems: ActiveDiagramItems, secondDiagramItems: ActiveDiagramItems): boolean {
        if (
            firstDiagramItems.showAbsoluteValues === secondDiagramItems.showAbsoluteValues &&
            firstDiagramItems.diagrams.length === secondDiagramItems.diagrams.length &&
            firstDiagramItems.channelDiagrams.length === secondDiagramItems.channelDiagrams.length
        ) {
            let diagramInfoMatch = true;
            firstDiagramItems.diagrams.forEach((firstDiagramItem, index) => {
                const secondDiagramItem = secondDiagramItems.diagrams[index];
                if (!_.isEqual(firstDiagramItem, secondDiagramItem)) {
                    diagramInfoMatch = false;
                }
            });
            firstDiagramItems.channelDiagrams.forEach((firstChannelDiagramItem, index) => {
                const secondChannelDiagramItem = secondDiagramItems.channelDiagrams[index];
                if (!_.isEqual(firstChannelDiagramItem, secondChannelDiagramItem)) {
                    diagramInfoMatch = false;
                }
            });
            return diagramInfoMatch;
        }
        return false;
    }

    private activeFiltersAreEqual(firstActiveFilters: BackendFilter[], secondActiveFilters: BackendFilter[]) {
        if (firstActiveFilters.length === secondActiveFilters.length) {
            let filtersMatch = true;
            firstActiveFilters.forEach((firstActiveFilter, index) => {
                const secondActiveFilter = secondActiveFilters[index];
                if (!_.isEqual(secondActiveFilter, firstActiveFilter)) {
                    filtersMatch = false;
                }
            });
            return filtersMatch;
        }
        return false;
    }

    public applyHistoryAndBookmarkSettings = (savedData: HistoryItem): void => {
        this.lastClickedItem = savedData;
        // triggers reaction to switch line/selectorTable store in mainStore before other changes get applied
        this.mainStore.activeAnalyticsTab = savedData.analyticsTab;
        transaction(() => {
            this.currentHistoryItemSettings[savedData.analyticsTab] = savedData;
            this.handlers.forEach((handler) => handler.applyHistory(savedData));
        });

        transaction(() => {
            this.handlers
                .filter((handler) => typeof handler.afterApplyHistory === 'function')
                .forEach((handler) => handler.afterApplyHistory && handler.afterApplyHistory(savedData));
        });
    };

    public addBookmarkEntry = (historyData: HistoryItem, name: string): void => {
        const bookmarkEntry: BookmarkItem = {
            ...historyData,
            name,
        };

        this.bookmarkItems.push(bookmarkEntry);
        this.removeHistoryItem(historyData.id);
        this.saveBookmarks();
    };

    public degradeBookmarkToHistoryItem = (bookmarkItem: BookmarkItem): void => {
        const {name, ...degradedBookmarkItem} = bookmarkItem;
        this.removeBookmarkItem(bookmarkItem.id);
        this.historyItems.unshift(degradedBookmarkItem);
        this.saveHistory();
    };

    private allowHistorySettingSave(historyEntry: HistoryItem): boolean {
        if (!historyEntry.channel) {
            return false;
        } else if (historyEntry.analyticsTab === null || historyEntry.analyticsTab === AnalyticsTab.Dashboard) {
            return false;
        } else if (historyEntry.diagramMode !== DiagramMode.Hourly) {
            return true;
        } else if (historyEntry.activeSearch.trim() !== '') {
            return true;
        } else if (historyEntry.activeFilters.length !== 0) {
            return true;
        } else if (historyEntry.activeDiagramItems.diagrams.length > 0 || historyEntry.activeDiagramItems.channelDiagrams.length > 0) {
            return true;
        } else if (historyEntry.tableLayout && historyEntry.tableLayout !== '') {
            return true;
        } else if (historyEntry.activeTableSort) {
            return true;
        }

        return false;
    }

    private removeHistoryItem(id: string): void {
        this.historyItems = this.historyItems.filter((item) => item.id !== id);
        this.saveHistory();
    }

    private removeBookmarkItem(id: string): void {
        this.bookmarkItems = this.bookmarkItems.filter((item) => item.id !== id);
        this.saveBookmarks();
    }

    public serializeMemoryItems = (memoryItems: HistoryOrBookmarkItem[]): SerializedMemoryItem[] => {
        const extractDateRange = (dateRange?: DateRange): TimestampRange | undefined => {
            if (!dateRange) {
                return undefined;
            }
            return {
                startDate: dateRange.startDate ? dateRange.startDate.valueOf() : undefined,
                endDate: dateRange.endDate ? dateRange.endDate.valueOf() : undefined,
            };
        };

        return memoryItems.map(
            (memoryItem: HistoryOrBookmarkItem): SerializedMemoryItem => {
                return {
                    ...memoryItem,
                    channel: memoryItem.channel ? memoryItem.channel : undefined,
                    mainDateRange: extractDateRange(memoryItem.mainDateRange),
                    compareDateRange: extractDateRange(memoryItem.compareDateRange),
                };
            }
        );
    };

    private getKpiIfValidOrDefault<T>(dataKey: T, analyticsTab: AnalyticsTab, defaultValue: T): T {
        if (!isKPI(dataKey)) {
            console.error(`Cannot parse to memoryItem attribute "${dataKey}" to KPI`);
            return defaultValue;
        }
        return this.checkIfDataKeyExistsForAnalyticsTab(dataKey, analyticsTab) ? dataKey : defaultValue;
    }

    public deserializeMemoryItem = (memoryItems?: SerializedMemoryItem[]): HistoryOrBookmarkItem[] => {
        if (memoryItems === undefined) {
            return [];
        }

        return memoryItems.map((memoryItem) => {
            return {
                ...memoryItem,
                diagramMode: Object.values(DiagramMode).includes(memoryItem.diagramMode) ? memoryItem.diagramMode : DiagramMode.Hourly,
                mainDateRange: this.convertTimestampRangeToDateRange(this.mainStore.timeIntervalStore.mainDateRange, memoryItem.mainDateRange),
                compareDateRange: this.convertTimestampRangeToDateRange(
                    this.mainStore.timeIntervalStore.compareDateRange,
                    memoryItem.compareDateRange
                ),
                activeTableSort: memoryItem.activeTableSort
                    ? {
                          ...memoryItem.activeTableSort,
                          attribute: this.getKpiIfValidOrDefault(
                              memoryItem.activeTableSort.attribute,
                              memoryItem.analyticsTab,
                              memoryItem.analyticsTab === AnalyticsTab.SearchView ? SearchtermKPI.SalesFromSearches : ProductKPI.Sales
                          ),
                      }
                    : undefined,
                activeDiagramItems: {
                    ...memoryItem.activeDiagramItems,
                    diagrams: memoryItem.activeDiagramItems.diagrams.filter((diagramItem) =>
                        this.checkIfDataKeyExistsForAnalyticsTab(diagramItem.kpi, memoryItem.analyticsTab)
                    ),
                },
                activeFilters: memoryItem.activeFilters.filter((filter) =>
                    this.checkIfDataKeyExistsForAnalyticsTab(filter.attribute, memoryItem.analyticsTab)
                ),
            };
        });
    };

    private convertTimestampRangeToDateRange(defaultDateRange: DateRange, timestampRange?: TimestampRange): DateRange {
        if (timestampRange === undefined) {
            return DateUtils.copyDateRange(defaultDateRange);
        }
        return {
            startDate: moment(timestampRange.startDate),
            endDate: moment(timestampRange.endDate),
        };
    }

    private saveBookmarks(): void {
        if (this.memoryItemsLoadedFromServer) {
            this.storage.setItem(userKey('bookmarkItems'), this.serializeMemoryItems(this.bookmarkItems));
        }
    }

    private saveHistory(): void {
        if (this.memoryItemsLoadedFromServer) {
            this.historyItems = this.historyItems.slice(0, 99);
            this.storage.setItem(userKey('historyItems'), this.serializeMemoryItems(this.historyItems));
        }
    }

    private searchFilter = (historyItem: Partial<BookmarkItem>): boolean => {
        const searchText = this.searchText.toLowerCase();
        if (searchText === '') {
            return true;
        }
        if (historyItem.name && historyItem.name.toLowerCase().indexOf(searchText) !== -1) {
            return true;
        }
        if (historyItem.channel && historyItem.channel.toLowerCase().indexOf(searchText) !== -1) {
            return true;
        }
        if (historyItem.analyticsTab && historyItem.analyticsTab.toLowerCase().indexOf(searchText) !== -1) {
            return true;
        }
        if (historyItem.activeSearch && historyItem.activeSearch.toLowerCase().indexOf(searchText) !== -1) {
            return true;
        }

        if (historyItem.activeDiagramItems !== undefined && historyItem.analyticsTab) {
            const {channelDiagrams, diagrams} = historyItem.activeDiagramItems;
            //@ts-ignore
            if (channelDiagrams.some((channelDiagram) => this.kpiLabelDoesMatchSearch(channelDiagram.kpi, historyItem.analyticsTab, searchText))) {
                return true;
            }
            //@ts-ignore
            if (diagrams.some((channelDiagram) => this.kpiLabelDoesMatchSearch(channelDiagram.kpi, historyItem.analyticsTab, searchText))) {
                return true;
            }
        }

        return false;
    };

    private kpiLabelDoesMatchSearch(kpi: KPI, analyticsTab: AnalyticsTab, search: string): boolean {
        const {selectorTableStoreSearch, selectorTableStoreProduct} = this.mainStore.getSearchtermAndProductSelectorTableStore();
        if (analyticsTab === AnalyticsTab.ProductView) {
            return selectorTableStoreProduct.tableColumnList.some(
                (productColumn) =>
                    productColumn.dataKey === kpi &&
                    (productColumn.label.toLowerCase().indexOf(search) !== -1 || productColumn.dataKey.indexOf(search) !== -1)
            );
        } else if (analyticsTab === AnalyticsTab.SearchView) {
            return selectorTableStoreSearch.tableColumnList.some(
                (searchColumn) =>
                    searchColumn.dataKey === kpi &&
                    (searchColumn.label.toLowerCase().indexOf(search) !== -1 || searchColumn.dataKey.indexOf(search) !== -1)
            );
        }
        return false;
    }
}

export default MemoryBarStore;



// WEBPACK FOOTER //
// ./src/stores/MemoryBarStore.ts